import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ErroApi } from '../../api/cliente';
import { useMovimentoReduzido } from '../../lib/movimento';

/**
 * Botão. O toque afunda o botão por CSS (`:active`, só transform); com
 * `carregando` o rótulo muda, a largura não, e `aria-busy` avisa o leitor.
 * Carregando, ele não vira `disabled`: o foco continua nele (quem usa teclado
 * não é jogado para o topo) e o clique é ignorado, inclusive o envio do form.
 */
export function Botao(
  { children, variante = 'primario', carregando, className, disabled, onClick, ...resto }:
  {
    children: ReactNode; variante?: 'primario' | 'secundario' | 'perigoso'; carregando?: boolean;
    /** React 19: `ref` chega como prop e vai para o <button> pelo `resto`. */
    ref?: React.Ref<HTMLButtonElement>;
  } &
    React.ButtonHTMLAttributes<HTMLButtonElement>,
) {
  const cor =
    variante === 'primario'
      ? 'botao-primario'
      : variante === 'perigoso' ? 'botao-perigoso' : 'botao-secundario';
  return (
    <button
      {...resto}
      disabled={disabled}
      aria-disabled={carregando || undefined}
      aria-busy={carregando || undefined}
      onClick={carregando ? (e) => e.preventDefault() : onClick}
      className={`botao ${cor} ${className ?? ''}`}
    >
      {children}
    </button>
  );
}

/**
 * Campo de formulario acessivel por construcao: label SEMPRE visivel (nunca
 * placeholder no lugar de label -- ele some quando a pessoa digita, e some
 * justamente para quem tem dificuldade de memoria de trabalho), erro ligado por
 * aria-describedby e aria-invalid no controle.
 */
export function Campo({
  id, rotulo, tipo = 'text', erro, dica, obrigatorio, ...resto
}: {
  id: string;
  rotulo: string;
  tipo?: string;
  erro?: string;
  dica?: string;
  obrigatorio?: boolean;
  ref?: React.Ref<HTMLInputElement>;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const idDica = dica ? `${id}-dica` : undefined;
  const idErro = erro ? `${id}-erro` : undefined;
  const descrito = [idDica, idErro].filter(Boolean).join(' ') || undefined;

  return (
    <div className="grupo-controle">
      <label htmlFor={id} className="rotulo-campo">
        {rotulo}
        {obrigatorio && (
          <>
            {' '}
            <span aria-hidden="true">*</span>
            <span className="so-leitor">(obrigatório)</span>
          </>
        )}
      </label>
      {dica && <p id={idDica} className="campo-dica">{dica}</p>}
      <input
        {...resto}
        id={id}
        type={tipo}
        required={obrigatorio}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descrito}
        className="controle"
      />
      {erro && <p id={idErro} className="campo-erro">{erro}</p>}
    </div>
  );
}

/**
 * Erro de envio do formulario. `role="alert"` para o leitor anunciar na hora.
 * Quem enxerga também precisa ver na hora: se o aviso nasce fora da área
 * visível (celular rolado até o botão, aviso lá em cima), a tela vai até ele.
 * `block: 'nearest'` não mexe na tela quando o aviso já está à vista, e o
 * `scroll-padding` do <html> desconta o cabeçalho e a barra de baixo.
 * Ainda assim, ponha o aviso junto do botão que dispara a ação: é ali que a
 * pessoa está olhando.
 */
export function AvisoErro({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const reduzido = useMovimentoReduzido();
  // Texto, não o nó: JSX novo a cada render rolaria a tela sem motivo.
  const texto = typeof children === 'string' || typeof children === 'number' ? String(children) : null;
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest', behavior: reduzido ? 'auto' : 'smooth' });
  }, [texto]); // só quando o aviso muda; `reduzido` só escolhe o jeito
  return <p ref={ref} role="alert" className="aviso-erro">{children}</p>;
}

/**
 * Cabeçalho operacional: título, resumo e metadado curto sem competir com a ação.
 */
export function CabecalhoTela({
  titulo,
  resumo,
  protocolo,
  acao,
}: {
  titulo: string;
  resumo?: ReactNode;
  protocolo?: string;
  acao?: ReactNode;
}) {
  return (
    <header className="cabecalho-tela">
      <div className="cabecalho-tela-conteudo">
        <h1>{titulo}</h1>
        {resumo && <div className="cabecalho-tela-resumo">{resumo}</div>}
      </div>
      {(protocolo || acao) && (
        <div className="cabecalho-tela-lateral">
          {protocolo && <span className="protocolo">{protocolo}</span>}
          {acao}
        </div>
      )}
    </header>
  );
}

/** Superfície operacional reutilizável: um cartão com o título na faixa de cima. */
export function Painel({
  titulo,
  children,
  className,
  tom = 'papel',
  id,
  focavel,
}: {
  titulo?: string;
  children: ReactNode;
  className?: string;
  tom?: 'papel' | 'registro' | 'sutil';
  id?: string;
  /** O título aceita foco por código, para levar a pessoa ao próximo passo. */
  focavel?: boolean;
}) {
  const rotuloId = id && titulo ? `${id}-titulo` : undefined;
  return (
    <section
      id={id}
      aria-labelledby={rotuloId}
      className={`painel painel-${tom} ${className ?? ''}`}
    >
      {titulo && (
        <h2 id={rotuloId} className="painel-titulo" tabIndex={focavel ? -1 : undefined}>{titulo}</h2>
      )}
      <div className="painel-conteudo">{children}</div>
    </section>
  );
}

/**
 * Maré calma: a ilustração do estado vazio. Um círculo de água parada com
 * duas ondas baixas e um brilho -- ainda não há nada ali, e a ação ao lado é
 * o que movimenta. Usa só tokens: no alto contraste vira traço sobre branco.
 */
export function IlustracaoVazia({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true" focusable="false">
      <circle cx="32" cy="32" r="30" fill="var(--color-primary-soft)" />
      <path d="M2.5 36c6 0 6-4 12-4s6 4 12 4 6-4 12-4 6 4 12 4 6-4 11-4V40a30 30 0 0 1-59 0z" fill="currentColor" opacity="0.18" />
      <path d="M12 40c4 0 4-3 8-3s4 3 8 3 4-3 8-3 4 3 8 3 4-3 8-3" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M18 48c3.5 0 3.5-2.5 7-2.5s3.5 2.5 7 2.5 3.5-2.5 7-2.5 3.5 2.5 7 2.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" opacity="0.55" />
      <circle cx="40" cy="20" r="6" fill="var(--color-coral)" />
      <circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.25" />
    </svg>
  );
}

/** Estado vazio ou de recuperação, sempre com uma próxima ação clara. */
export function EstadoVazio({
  titulo,
  children,
  acao,
}: {
  titulo: string;
  children: ReactNode;
  acao?: ReactNode;
}) {
  return (
    <section className="estado-vazio">
      <IlustracaoVazia className="estado-vazio-marca" />
      <div>
        <h2>{titulo}</h2>
        <div className="estado-vazio-texto">{children}</div>
        {acao && <div className="estado-vazio-acao">{acao}</div>}
      </div>
    </section>
  );
}

/**
 * A lista não carregou. Nunca mostrar o estado vazio nesse caso: "Você não
 * tem pedidos" quando a rede caiu faz a pessoa acreditar que perdeu tudo.
 */
export function EstadoErro({
  children = 'Não conseguimos carregar esta parte agora. Confira sua conexão e tente de novo.',
  aoTentarDeNovo,
  tentando,
}: {
  children?: ReactNode;
  aoTentarDeNovo: () => void;
  tentando?: boolean;
}) {
  return (
    <div role="alert" className="estado-erro">
      <p>{children}</p>
      <Botao type="button" variante="secundario" carregando={tentando} onClick={aoTentarDeNovo}>
        {tentando ? 'Tentando…' : 'Tentar de novo'}
      </Botao>
    </div>
  );
}

/** Texto de estado que nunca depende só de cor ou de um símbolo. */
export function Selo({ children, tom = 'neutro' }: {
  children: ReactNode;
  tom?: 'neutro' | 'sucesso' | 'atencao' | 'erro';
}) {
  return <span className={`selo selo-${tom}`}>{children}</span>;
}

/**
 * Esqueleto de carregamento. O leitor de tela ouve "Carregando…" uma vez; as
 * barras são só desenho. A altura reserva o espaço da lista, para o conteúdo
 * chegar sem empurrar a página (CLS).
 */
export function Esqueleto({ linhas = 3, rotulo = 'Carregando…' }: { linhas?: number; rotulo?: string }) {
  return (
    <div role="status" className="esqueleto-grupo">
      <span className="so-leitor">{rotulo}</span>
      {Array.from({ length: linhas }, (_, i) => (
        <span key={i} aria-hidden="true" className="esqueleto" style={{ inlineSize: `${100 - ((i * 17) % 40)}%` }} />
      ))}
    </div>
  );
}

/**
 * Etapas de um processo (pedido, cadastro). A atual tem `aria-current="step"`;
 * o estado também vem escrito para o leitor, não só pela cor do selo.
 */
export function Etapas({ etapas, atual, rotulo }: { etapas: string[]; atual: number; rotulo: string }) {
  return (
    <ol className="passos-formulario" aria-label={rotulo}>
      {etapas.map((nome, i) => {
        const estado = i < atual ? 'concluido' : i === atual ? 'atual' : 'futuro';
        return (
          <li key={nome} className="passo-formulario" data-estado={estado} aria-current={estado === 'atual' ? 'step' : undefined}>
            <span className="passo-formulario-numero" aria-hidden="true">{i + 1}</span>
            <span>
              {nome}
              <span className="so-leitor">{estado === 'concluido' ? ' (feito)' : estado === 'atual' ? ' (agora)' : ''}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Perguntas e respostas. <details> nativo: abre com teclado e leitor sem
 * script; a altura anima por CSS onde o navegador sabe (::details-content).
 */
export function Acordeao({ itens }: { itens: { pergunta: string; resposta: ReactNode }[] }) {
  return (
    <div className="acordeao">
      {itens.map((item) => (
        <details key={item.pergunta}>
          <summary>{item.pergunta}</summary>
          <div>{item.resposta}</div>
        </details>
      ))}
    </div>
  );
}

/**
 * Ação que não tem volta, em dois toques: o primeiro pergunta, o segundo faz.
 *
 * A pergunta aparece no lugar do botão, dizendo o efeito em frase. O foco vai
 * para "Não" (a opção segura, que também lê a pergunta) e volta ao botão
 * quando a pessoa desiste, por clique ou Esc. Se `aoConfirmar` devolver uma
 * promessa, a pergunta só fecha quando ela der certo; se falhar, continua
 * aberta com o erro escrito ali mesmo, onde o foco está. Quem chama não deve
 * repetir esse erro em outro lugar.
 *
 * Depois de confirmar, o botão costuma sumir (o item mudou de estado):
 * `focarDepois` diz para onde o foco vai, para ele não cair no fim da página.
 */
export function ConfirmarAcao({
  rotulo, pergunta, sim, aoConfirmar, carregando, desabilitado, variante = 'perigoso', focarDepois,
  falha = 'Não deu certo agora. Tente de novo.',
}: {
  rotulo: ReactNode;
  pergunta: ReactNode;
  sim: string;
  aoConfirmar: () => unknown;
  carregando?: boolean;
  desabilitado?: boolean;
  variante?: 'primario' | 'secundario' | 'perigoso';
  focarDepois?: () => void;
  /** Frase para falha sem mensagem do servidor (rede, por exemplo). */
  falha?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const abrir = useRef<HTMLButtonElement>(null);
  const nao = useRef<HTMLButtonElement>(null);
  const anterior = useRef(aberto);
  const confirmou = useRef(false);
  const idPergunta = useId();
  const idErro = useId();

  useEffect(() => {
    if (anterior.current === aberto) return;
    anterior.current = aberto;
    if (aberto) { nao.current?.focus(); return; }
    if (confirmou.current && focarDepois) focarDepois();
    else abrir.current?.focus();
    confirmou.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  // A pergunta descreve o estado de agora. Se ele deixou de valer (o texto
  // obrigatório foi apagado, por exemplo), ela fecha em vez de mudar calada.
  useEffect(() => {
    if (desabilitado && aberto && !carregando) setAberto(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desabilitado]);

  async function confirmar() {
    setErro(null);
    try {
      await aoConfirmar();
      confirmou.current = true;
      setAberto(false);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : falha);
    }
  }

  if (!aberto) {
    return (
      <Botao type="button" variante={variante} ref={abrir} disabled={desabilitado}
        onClick={() => { setErro(null); setAberto(true); }}>
        {rotulo}
      </Botao>
    );
  }
  return (
    <div
      role="group" aria-labelledby={idPergunta} className="confirmar-acao"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !carregando) { e.stopPropagation(); setAberto(false); }
      }}
    >
      <p id={idPergunta}>{pergunta}</p>
      {erro && <p id={idErro} role="alert" className="campo-erro">{erro}</p>}
      <div className="confirmar-acao-botoes">
        <Botao type="button" variante="perigoso" carregando={carregando}
          aria-describedby={erro ? idErro : undefined} onClick={() => void confirmar()}>
          {carregando ? 'Um momento…' : sim}
        </Botao>
        <Botao type="button" variante="secundario" ref={nao} disabled={carregando}
          aria-describedby={idPergunta} onClick={() => setAberto(false)}>
          Não
        </Botao>
      </div>
    </div>
  );
}
